import AxiosStatic from "axios";
import store from "../store";
import UserLogin from "./user-login"
import UserSignup from "./user-signup";
import ResetPassword from "./reset-password";
import { FullDetailsError } from "./fullDetailsError";

class AuthService {
  $http = AxiosStatic

  login(user: UserLogin) {
    return this.$http
      .post("/api/account/login", user)
      .then(response => {
        if (response.data) {
          if (response.data.authenticated) {
            store.dispatch("updateLogin", response.data);
          }
          return response.data;
        }
        else {
          throw new Error("An unexpected error has occurred");
        }
      })
      .catch(error => {
        this.formatErrorsAndRethrow(error);
      })
  }

  async impersonateUser(businessID: number) {
    const result = await this.$http.post("/api/account/Impersonate?businessID=" + businessID);
    
    if (result?.data) {
      store.dispatch("updateImpersonateLogin", result.data);
    }
    
    return result.data;
  }

  checkGUIDValid(GUID: string) {
    return this.$http
      .get("/api/account/checkGUIDValid?GUID=" + GUID)
      .then(response => {
        return response.data;
      })
      .catch(this.formatErrorsAndRethrow);
  }

  async getXeroConfig(): Promise<any> {
    return this.$http
      .post("/api/account/getXeroConfig")
      .then(response => {
        if (response.data) {
          store.dispatch("updateXeroConfig", response.data);
        } else {
          throw new Error("An unexpected error has occurred");
        }
      })
      .catch(this.formatErrorsAndRethrow);
  }

  setTwoFactorSkipped(): Promise<any> {
    return this.$http.post("/api/account/setTwoFactorSkipped");
  }

  RedirectToXero(scopes: string, referral: boolean) {
    let xeroClientID = store.getters.xeroClientID;

    if (!xeroClientID) {
      this.getXeroConfig()
      .then(() => {
        xeroClientID = store.getters.xeroClientID;
        this.RedirectToXeroAction(scopes, referral);
      })
      .catch(this.formatErrorsAndRethrow);
    }
    else {
      this.RedirectToXeroAction(scopes, referral);
    }
  }

  RedirectToXeroAction(scopes: string, referral: boolean) {
    let xeroClientID = store.getters.xeroClientID;
    let xeroRedirectURI = store.getters.xeroRedirectURI;
    let xeroReferralRedirectURI = store.getters.xeroReferralRedirectURI;
    let xeroTenantRedirectURI = store.getters.xeroTenantRedirectURI;

    let redirectURI = xeroRedirectURI;
    
    if (scopes.includes("accounting")) {
      redirectURI = xeroTenantRedirectURI;
    }
    else if (referral) {
      redirectURI = xeroReferralRedirectURI;
    }

    const xeroRedirectLocation = "https://login.xero.com/identity/connect/authorize?response_type=code&client_id=" + xeroClientID + "&redirect_uri=" + redirectURI + "&scope=" + scopes;
    location.href = xeroRedirectLocation;
  }

  async CheckTwoFactorEnabled(): Promise<any> {
    return this.$http
      .post("/api/account/checkTwoFactorEnabled")
      .then(response => {
        return response.data;
      })
      .catch(this.formatErrorsAndRethrow);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async xeroLogin(code: string, referral: boolean, userToken: boolean): Promise<any> {
    return this.$http
      .post("/api/account/xeroLogin", { code: code, referral: referral, userToken: userToken })
      .then(response => {
        if (response.data) {
          if (!response.data.tooManyRequests) {
            if (!response.data.multipleConnections) {
              store.dispatch("updateLogin", response.data);
            }
          }
          return response.data;
        }
        else {
          throw new Error("An unexpected error has occurred");
        }
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async refreshToken(): Promise<any> {
    const userID = store.getters.userID;
    if (userID) {
      try {
        const response = await this.$http.post("/api/account/refreshToken?userId=" + userID);
        if (response.data) {
          store.dispatch("updateLogin", response.data);
        }
        return response;
      } catch (err) {
        this.formatErrorsAndRethrow(err);
      }
    }
    return { data: { authenticated: false } };
  }

  async logout() {
    const result = this.$http.post("/api/account/LogOff")
    result.then(function () {
      store.dispatch("clearLogin");
      store.dispatch("clearImpersonateLogin");
    });

    return result;
  }

  async impersonateUserLogOut() {
    return this.$http
      .post("/api/account/LogOffImpersonateUser")
      .then(() => {
        store.dispatch("clearImpersonateLogin");
      })
      .catch(this.formatErrorsAndRethrow)
  }

  async Request2FAProtection(method: string, value: string) {
    return this.$http
      .post("/api/account/Request2FAProtection?method=" + method + "&value=" + value)
      .catch(this.formatErrorsAndRethrow);
  }

  async Send2FAChallenge(email: string, method: string, guid: any) {
    let url = "/api/account/Send2FAChallenge?username=" + encodeURIComponent(email) + "&method=" + method;
    if (guid) {
      url += "&guid=" + guid;
    }
    return this.$http
      .post(url)
      .catch(this.formatErrorsAndRethrow);
  }

  async Send2FAChallengeByID(userID: number, method: string, guid: any) {
    let url = "/api/account/Send2FAChallengeByID?userID=" + userID + "&method=" + method;
    if (guid) {
      url += "&guid=" + guid;
    }
    return this.$http
      .post(url)
      .catch(this.formatErrorsAndRethrow);
  }

  async RemoveTwoFactorMethod(userID: number, method: string, code: string) {
    return this.$http
      .post("/api/account/RemoveTwoFactorMethod?userID=" + userID + "&method=" + method + "&code=" + code)
      .catch(this.formatErrorsAndRethrow);
  }

  async Apply2FAProtection(code: string) {
    return this.$http
      .post("/api/account/Apply2FAProtection?code=" + code)
      .catch(this.formatErrorsAndRethrow);
  }

  getTwoFactorMethods(email: string, guid: any) {
    let url = "/api/account/GetTwoFactorMethods?username=" + encodeURIComponent(email);
    if (guid) {
      url += "&guid=" + guid;
    }
    return this.$http
      .post(url)
      .then(response => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error("An unexpected error has occurred");
        }
      })
      .catch(this.formatErrorsAndRethrow);
  }

  getTwoFactorMethodsByID(userID: number, guid: any) {
    let url = "/api/account/GetTwoFactorMethodsByID?userID=" + userID;
    if (guid) {
      url += "&guid=" + guid;
    }
    return this.$http
      .post(url)
      .then(response => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error("An unexpected error has occurred");
        }
      })
      .catch(this.formatErrorsAndRethrow);
  }

  signup(user: UserSignup) {
    return this.$http.post("/api/account/signup", user)
      .catch(this.formatErrorsAndRethrowWithStatus);
  }
  
  affiliateSignup(user: UserSignup) {
    return this.$http.post("/api/account/affiliateSignup", user)
      .catch(this.formatErrorsAndRethrowWithStatus);
  }

  requestRecovery(email: string) {
    return this.$http.post("/api/account/requestRecovery", {
      email: email
    }).then(response => {
      if (response.data) {
        return response.data;
      } else {
        throw new Error("An unexpected error has occurred");
      }
    })
      .catch(this.formatErrorsAndRethrow)
  }

  resetPassword(GUID: string) {
    return this.$http.post("/api/account/resetPassword", {
      GUID: GUID
    }).then(response => {
      if (response.data.user) {
        store.commit("setUser", response.data.user);
      } else {
        throw new Error("An unexpected error has occurred");
      }
    })
      .catch(this.formatErrorsAndRethrow)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accountVerification(resetPassword: ResetPassword) {
    return this.$http.post("/api/account/resetPassword", {
      Email: resetPassword.email,
      Password: resetPassword.password,
      ConfirmPassword: resetPassword.confirmPassword,
      Code: resetPassword.code,
      FinalisationAccount: resetPassword.finalisationAccount
    }).then(response => {
      if (response.data) {
        return response.data;
      } else {
        throw new Error("An unexpected error has occurred");
      }
    })
    .catch(error => {
      this.formatErrorsAndRethrow(error)
    })
  }

  async switchOrganisation(businessID: number) {
    store.commit("setSelectedPeriod", "Current");
    return await this.$http
      .post("/api/account/switchOrganisation?businessID=" + businessID)
      .then((response) => {
        if (response.data) {
          if (store.getters.isAdmin) {
            store.dispatch("updateImpersonateLogin", response.data)
          }
          else {
            store.dispatch("updateLogin", response.data)
          }
        }

        return response.data;
      })
      .catch(this.formatErrorsAndRethrow);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatErrorsAndRethrow(error: any) {
    if (error.response) {
      let err = error.response.data;
      if (err.errors) {
        err = [...Object.keys(error.response.data.errors).map(
          key => error.response.data.errors[key]
        )];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        err = err.reduce((flat: any, list: any) => flat.concat(list));
        console.log(err);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        err = err.reduce((val: any, arr: any) => `${val} \r\n${arr}`, "")
      }
      throw new Error(err);
    } else {
      throw new Error("An unexpected error has occurred");
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatFirstErrorAndRethrow(error: any) {
    if (error.response) {
      let err = error.response.data;
      if (err.errors) {
        err = [...Object.keys(error.response.data.errors).map(
          key => error.response.data.errors[key]
        )];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        err = err.reduce((flat: any, list: any) => flat.concat(list));
        console.log(err);
      }
      throw new Error(err[0]);
    } else {
      throw new Error("An unexpected error has occurred");
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatErrorsAndRethrowWithStatus(error: any) {
    if (error.response) {
      const err = error.response.data;
      throw new FullDetailsError(err, error).getExtraDetails();
    } else {
      throw new Error("An unexpected error has occurred");
    }
  }
}
export default new AuthService()
